import type { StaticImageData } from "next/image";
import type { ReactNode } from "react";
import { createContext, useContext, useMemo, useState } from "react";

export enum ModalTypeEnum {
  CARDGALLERY = "card-gallery",
  CONTENT = "content",
}

type ModalContextProps = {
  isModalOpen: boolean;
  modalType?: ModalTypeEnum;
  openModal: (
    modalType: ModalTypeEnum,
    props?: {
      index?: number;
      cards?: StaticImageData[];
      children?: any;
    },
  ) => void;
  closeModal: () => void;
  props?: {
    index?: number;
    cards?: StaticImageData[];
    children?: any;
  };
};

const ModalContext = createContext<ModalContextProps>({} as ModalContextProps);

const ModalProvider = ({ children }: { children: ReactNode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalType, setModalType] = useState<ModalTypeEnum | undefined>(undefined);
  const [props, setProps] = useState<
    | {
        index?: number;
        cards?: StaticImageData[];
        //
        children?: any;
      }
    | undefined
  >();

  function openModal(
    type: ModalTypeEnum,
    props?: {
      index?: number;
      cards?: StaticImageData[];
      //
      children?: any;
    },
  ) {
    setIsModalOpen(true);
    setProps(props);
    setModalType(type);
  }

  function closeModal() {
    setIsModalOpen(false);
    setProps(undefined);
    setModalType(undefined);
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const memoisedValue = useMemo(() => {
    return {
      isModalOpen,
      modalType,
      openModal,
      closeModal,
      props,
    };
  }, [isModalOpen, modalType, props]);

  return <ModalContext.Provider value={memoisedValue}>{children}</ModalContext.Provider>;
};

const useModal = () => useContext(ModalContext);
export { ModalProvider, useModal };
